import { Injectable } from '@angular/core';
import { collection, doc, setDoc, getDoc, updateDoc, deleteDoc, getDocs, CollectionReference, DocumentData, query, where, QuerySnapshot } from 'firebase/firestore';
import { FirebaseService } from './firebase.service';
import { UserService } from './user.service';
import { Observable, forkJoin, from, map, switchMap } from 'rxjs';
import { environment } from 'env/environment';
import { handleReduceDataModel, handleReduceFlightOptions } from 'app/utils/flight/flight-data-utils';
import { normalizeBooleanFlag } from 'app/utils/boolean-normalizer';
import { getAuth } from 'firebase/auth';
import { DEFAULT_GROQ_CHAT_MODEL } from 'app/constants/ai-model-constants';

export interface FlightOption {
    id?: string;
    [key: string]: any; // TS TODO: Finalize interface
}

export interface UserDataModel {
    [key: string]: any; // TS TODO: Finalize interface
}

export interface MatchResult {
    agentResponse: string;
    optimalMatches: string[]; // Array of FlightOption IDs
    similarMatches: { description: string; options: string[] }[]; // Array of close match groups with IDs
}

interface FlightDisplayOptionGroup {
    description: string;
    options: FlightOption[];
}

interface FlightDisplayMapping {
    optimalMatches: FlightOption[];
    similarMatches: FlightDisplayOptionGroup[];
}

@Injectable({ providedIn: 'root' })
export class FlightOptionService {
    private flightsRef: CollectionReference<DocumentData>;
    private searchRef: CollectionReference<DocumentData>;

    constructor(private firebaseService: FirebaseService, private userService: UserService) {
        this.flightsRef = collection(this.firebaseService.firestore, 'flights');
        this.searchRef = collection(this.firebaseService.firestore, 'flight-search');
    }

    private getUserProfile(): any {
        const user = this.userService.getUserProfile();
        if (!user) throw new Error('User not authenticated.');
        return user;
    }

    async createFlightOption(dataModel: any, metadataOverrides: Record<string, any> = {}): Promise<any> {
        const userProfile = await this.getUserProfile();
        const isCreatorVerified = this.hasAccessMembership(userProfile);

        const firestoreDoc = await this.flattenDataModel(dataModel, {
            creatorId: userProfile.id,
            creatorDisplayName: userProfile.displayName,
            isCreatorVerified,
            verifiedMemberships: userProfile?.verifiedMemberships || [],
            memberships: userProfile.memberships, // TODO: Once these are actual, likely remove memberId for security.
            creatorLinkedInMatrix: userProfile?.linkedInMatrix || null,
            ...metadataOverrides,
        });

        console.log('FLATTENED FLIGHT DOC');

        const docRef = doc(this.flightsRef);

        const id = docRef.id;
        const flightOption = { id, ...firestoreDoc };

        await setDoc(docRef, firestoreDoc);
        await setDoc(doc(this.searchRef, id), this.buildFlightSearchProjection(id, firestoreDoc));
        return flightOption;
    }

    async getFlightOption(optionId: string): Promise<FlightOption | null> {
        const docRef = doc(this.flightsRef, optionId);
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
            return { id: optionId, ...snapshot.data() } as FlightOption;
        }
        return null;
    }

    async updateFlightOption(optionId: string, updates: Partial<FlightOption>): Promise<void> {
        const docRef = doc(this.flightsRef, optionId);
        await updateDoc(docRef, updates);
        const updated = await this.getFlightOption(optionId);
        if (updated) {
            await setDoc(doc(this.searchRef, optionId), this.buildFlightSearchProjection(optionId, updated), { merge: true });
        }
    }

    async deleteFlightOption(optionId: string): Promise<void> {
        await deleteDoc(doc(this.flightsRef, optionId));
        await deleteDoc(doc(this.searchRef, optionId)).catch(() => undefined);
    }

    async getAllFlightOptions(): Promise<FlightOption[]> {
        const snapshot = await getDocs(this.flightsRef);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FlightOption));
    }

    getMatchingFlightOptions(dataModel: UserDataModel, daysBuffer: number = 7): Observable<FlightOption[]> {
        void daysBuffer;
        return from(this.searchFlightOptions(dataModel));
    }

    private async searchFlightOptions(dataModel: UserDataModel): Promise<FlightOption[]> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('User not authenticated.');
        }
        const idToken = await user.getIdToken();
        const url = `https://${environment.apiBaseURL}.cloudfunctions.net/searchFlightOptions`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ dataModel }),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`Flight search failed: ${response.status} ${text}`);
        }
        const payload = await response.json();
        return Array.isArray(payload?.options) ? payload.options : [];
    }

    private getDateRange(dataModel: UserDataModel, daysBuffer: number): { startDate: string; endDate: string } {
        const exactDate = dataModel.travelTiming?.departureDateExact;
        const rangeStart = dataModel.travelTiming?.departureDateRange?.start;
        const rangeEnd = dataModel.travelTiming?.departureDateRange?.end;

        // Validate that at least one date field is provided
        if (!exactDate && !rangeStart && !rangeEnd) {
            // Default to a 30-day range from today if no date is provided
            const today = new Date();
            const defaultEnd = new Date(today);
            defaultEnd.setDate(today.getDate() + 30);
            return {
                startDate: today.toISOString().split('T')[0],
                endDate: defaultEnd.toISOString().split('T')[0],
            };
        }

        let start: Date;
        let end: Date;

        // Prioritize range over exact date
        if (rangeStart && rangeEnd) {
            start = new Date(rangeStart);
            end = new Date(rangeEnd);
        } else if (exactDate) {
            start = new Date(exactDate);
            end = new Date(exactDate);
        } else if (rangeStart) {
            start = new Date(rangeStart);
            end = new Date(rangeStart);
        } else {
            start = new Date(rangeEnd!);
            end = new Date(rangeEnd!);
        }

        // Apply daysBuffer only if an exact date is used (not a range)
        if (!rangeStart || !rangeEnd) {
            start.setDate(start.getDate() - daysBuffer);
            end.setDate(end.getDate() + daysBuffer);
        }

        return {
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0],
        };
    }

    async refineFlightOptionsLLM(currentDataModel: UserDataModel, flightOptions: FlightOption[], exactMatchCount: number): Promise<MatchResult> {

        const dataModel = await handleReduceDataModel(currentDataModel);

        console.log('the data model', dataModel)

        const options = handleReduceFlightOptions(flightOptions);

        const url = `https://${environment.apiBaseURL}.cloudfunctions.net/getRefinedFlightOptions`;
        const payload = {
            dataModel,
            options,
            exactMatchCount,
            model: DEFAULT_GROQ_CHAT_MODEL
        };

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error refining options: ${response.status} - ${errorText}`);
            }

            const refinedOptions: MatchResult[] = await response.json();
            return refinedOptions[0]; // Assuming LLM returns an array with one object
        } catch (error) {
            console.error('LLM Refinement Error:', error);
            throw error;
        }
    }

    buildFlightDisplayOptions(
        exactMatches: FlightOption[],
        otherOptions: FlightOption[],
        llmResponse: {
            agentResponse: string,
            similarMatches: { description: string; options: string[] }[];
        }
    ): FlightDisplayMapping {
        // Turn flight list into a quick lookup map
        const flightMap = new Map(otherOptions.map(flight => [flight.id, flight]));

        // Map exact matches
        const optimalMatches = exactMatches;

        // Map similar matches
        const similarMatches = llmResponse.similarMatches.map(group => ({
            description: group.description,
            options: group.options
                .map(id => flightMap.get(id))
                .filter((f): f is FlightOption => !!f)
        }));

        return { optimalMatches, similarMatches };
    }

    async isFlightDataModelValid(dataModel: any): Promise<boolean> { // TS TODO: expand ts
        const departure = dataModel?.departureData?.locationName;
        const departureAirport = dataModel?.departureData?.airportCode;
        const arrivalPrecise = dataModel?.arrivalData?.preciseDestination?.locationName;
        const arrivalGeneral = dataModel?.arrivalData?.generalDestination?.destinationType;
        const seatsNeeded = dataModel?.seatsNeeded;
        const seatsOffered = dataModel?.reservationData?.seatsOffered;
        const exactDate = dataModel?.travelTiming?.departureDateExact;
        const dateRangeStart = dataModel?.travelTiming?.departureDateRange?.start;
        const dateRangeEnd = dataModel?.travelTiming?.departureDateRange?.end;
        const exactTime = dataModel?.travelTiming?.departureTimeExact;
        const timeRangeStart = dataModel?.travelTiming?.departureTimeRange?.start;
        const timeRangeEnd = dataModel?.travelTiming?.departureTimeRange?.end;

        const isPositiveSeatCount = (value: unknown): value is number =>
            typeof value === 'number' && Number.isInteger(value) && value > 0;
        const isExistingReservation = dataModel?.reservationData?.isExistingReservation === true;
        const hasLocations = !!departure && !!departureAirport && (!!arrivalPrecise || !!arrivalGeneral);
        const hasSeats = isExistingReservation
            ? isPositiveSeatCount(seatsOffered) && !isPositiveSeatCount(seatsNeeded)
            : isPositiveSeatCount(seatsNeeded) && !isPositiveSeatCount(seatsOffered);
        const hasDates = !!exactDate || (!!dateRangeStart && !!dateRangeEnd);
        const hasTime = !!exactTime || (!!timeRangeStart && !!timeRangeEnd);

        return hasLocations && hasSeats && hasDates && hasTime;
    }

    async flattenDataModel(dataModel, metadata) {
        const rawMembership = dataModel?.userData?.membership;
        const isPetOk = normalizeBooleanFlag(dataModel?.userData?.preferences?.isPetOk);
        const isChildrenOk = normalizeBooleanFlag(dataModel?.userData?.preferences?.isChildrenOk);
        const isBringingPets = normalizeBooleanFlag(dataModel?.isBringingPets);
        const isBringingChildren = normalizeBooleanFlag(dataModel?.isBringingChildren);
        const isQuietFlightOnly = normalizeBooleanFlag(dataModel?.userData?.preferences?.isQuietFlightOnly);
        const membership = Array.isArray(rawMembership) ? rawMembership[0] || {} : rawMembership || {};
        const metaMemberships = Array.isArray(metadata?.memberships) ? metadata.memberships : [];
        const metaPrimary = metaMemberships[0] || {};

        const membershipOrganization = membership.organization ?? membership.provider ?? metaPrimary.organization ?? metaPrimary.provider ?? null;
        const aircraftTier = membership.aircraftTier ?? membership.tier ?? metaPrimary.aircraftTier ?? metaPrimary.tier ?? null;
        const aircraft = membership.aircraft ?? membership.defaultAircraft?.name ?? metaPrimary.aircraft ?? metaPrimary.defaultAircraft?.name ?? null;

        let aircraftMaxSeats =
            membership.aircraftMaxSeats ??
            membership.seatingConfiguration ??
            membership.defaultAircraft?.defaultSeats ??
            metaPrimary.aircraftMaxSeats ??
            metaPrimary.seatingConfiguration ??
            metaPrimary.defaultAircraft?.defaultSeats ??
            environment.defaults?.defaultAircraftSeats ?? null;

        return {
            isActive: metadata.isActive ?? true,
            isVisible: metadata.isVisible ?? true,
            creatorId: metadata.creatorId,
            isCreatorVerified: metadata.isCreatorVerified ?? false,
            creatorDisplayName: metadata.creatorDisplayName,
            creatorLinkedInMatrix: metadata.creatorLinkedInMatrix || [],
            memberships: metadata.memberships,

            createdAt: metadata.createdAt ?? new Date(),
            updatedAt: metadata.updatedAt ?? new Date(),

            isPreciseDestination: dataModel.isPreciseDestination,
            isInternational: dataModel.isInternational,
            isBringingPets: isBringingPets ?? false,
            isBringingChildren: isBringingChildren ?? false,
            seatsNeeded: dataModel.seatsNeeded,
            isValid: dataModel.isValid,

            departureLocationName: dataModel.departureData.locationName,
            departureAirportName: dataModel.departureData.airportName,
            departureAirportCode: dataModel.departureData.airportCode,

            arrivalLocationName: dataModel.arrivalData.preciseDestination?.locationName ?? null,
            arrivalAirportName: dataModel.arrivalData.preciseDestination?.airportName ?? null,
            arrivalAirportCode: dataModel.arrivalData.preciseDestination?.airportCode ?? null,
            arrivalPreciseState: dataModel.arrivalData.preciseDestination?.state ?? null,
            arrivalPreciseCountry: dataModel.arrivalData.preciseDestination?.country ?? null,
            arrivalPreciseType: dataModel.arrivalData.preciseDestination?.destinationType ?? null,
            arrivalGeneralType: dataModel.arrivalData.generalDestination?.destinationType ?? null,
            arrivalGeneralCountry: dataModel.arrivalData.generalDestination?.country ?? null,
            arrivalGeneralState: dataModel.arrivalData.generalDestination?.state ?? null,

            timeZone: dataModel.travelTiming.timeZone,
            departureDateExact: dataModel.travelTiming.departureDateExact ?? null,
            departureDateRangeStart: dataModel.travelTiming.departureDateRange?.start ?? null,
            departureDateRangeEnd: dataModel.travelTiming.departureDateRange?.end ?? null,
            departureTimeExact: dataModel.travelTiming.departureTimeExact ?? null,
            departureTimeRangeStart: dataModel.travelTiming.departureTimeRange?.start ?? null,
            departureTimeRangeEnd: dataModel.travelTiming.departureTimeRange?.end ?? null,
            estimatedFlightHours: dataModel.travelTiming.estimatedFlightHours ?? null,

            isQuietFlightOnly: isQuietFlightOnly ?? false,
            isPetOk: isPetOk ?? true,
            isChildrenOk: isChildrenOk ?? true,
            membershipOrganization,
            aircraftTier,
            aircraft,
            aircraftMaxSeats,

            isExistingReservation: dataModel.reservationData.isExistingReservation,
            expectedMood: dataModel.reservationData.expectedMood ?? null,
            seatsOffered: dataModel.reservationData.seatsOffered ?? null,
        };
    }

    private normalizeKey(value: unknown): string {
        return String(value || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    private positiveNumber(value: unknown): number | null {
        const parsed = typeof value === 'number' ? value : Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }

    private membershipHasAircraft(value: unknown): boolean {
        if (!value || typeof value !== 'object') {
            return false;
        }
        const membership = value as {
            aircraft?: unknown;
            aircraftMaxSeats?: unknown;
            defaultAircraft?: unknown;
            seatingConfiguration?: unknown;
        };

        return Boolean(
            membership.defaultAircraft ||
            membership.aircraft ||
            this.positiveNumber(membership.aircraftMaxSeats) ||
            this.positiveNumber(membership.seatingConfiguration)
        );
    }

    private flightOwnerHasAircraft(flight: Record<string, any>): boolean {
        if (Array.isArray(flight.memberships)) {
            return flight.memberships.some((membership: unknown) => this.membershipHasAircraft(membership));
        }

        return Boolean(
            flight.aircraft ||
            this.positiveNumber(flight.aircraftMaxSeats) ||
            this.positiveNumber(flight.seatsOffered)
        );
    }

    private buildActiveDateKeys(startValue: string | null | undefined, endValue: string | null | undefined, maxDays = 30): string[] {
        if (!startValue) {
            return [];
        }
        const start = new Date(`${startValue}T00:00:00`);
        const end = new Date(`${endValue || startValue}T00:00:00`);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return [];
        }
        const keys: string[] = [];
        const cursor = new Date(start.getTime());
        const pad2 = (value: number) => value.toString().padStart(2, '0');
        while (cursor.getTime() <= end.getTime() && keys.length < maxDays) {
            keys.push(`${cursor.getFullYear()}-${pad2(cursor.getMonth() + 1)}-${pad2(cursor.getDate())}`);
            cursor.setDate(cursor.getDate() + 1);
        }
        return keys;
    }

    private buildDestinationKeys(source: Record<string, any>): string[] {
        const keys = new Set<string>();
        if (source.arrivalAirportCode) {
            keys.add(`airport:${this.normalizeKey(source.arrivalAirportCode)}`);
        }
        if (source.arrivalLocationName) {
            keys.add(`place:${this.normalizeKey(source.arrivalLocationName)}`);
        }
        if (source.arrivalPreciseType) {
            keys.add(['type', source.arrivalPreciseType, source.arrivalPreciseCountry, source.arrivalPreciseState].map(v => this.normalizeKey(v)).filter(Boolean).join(':'));
        }
        if (source.arrivalGeneralType) {
            keys.add(['type', source.arrivalGeneralType, source.arrivalGeneralCountry, source.arrivalGeneralState].map(v => this.normalizeKey(v)).filter(Boolean).join(':'));
        }
        return Array.from(keys).filter(Boolean);
    }

    buildFlightSearchProjection(id: string, flight: Record<string, any>): Record<string, any> {
        const start = flight.departureDateExact || flight.departureDateRangeStart;
        const end = flight.departureDateExact || flight.departureDateRangeEnd;
        const activeDateKeys = this.buildActiveDateKeys(start, end);
        const isActive = flight.isActive !== false;
        const isVisible = flight.isVisible !== false;

        return {
            flightId: id,
            ownerUid: flight.creatorId,
            status: isActive ? 'active' : 'inactive',
            isSearchable: isActive && isVisible && activeDateKeys.length > 0,
            visibilityMode: isVisible ? 'public' : 'private',
            providerKey: this.normalizeKey(flight.membershipOrganization || 'independent') || 'independent',
            ownerHasAircraft: this.flightOwnerHasAircraft(flight),
            allowedProviderKeys: Array.isArray(flight.allowedProviderKeys) ? flight.allowedProviderKeys : [],
            departureKey: this.normalizeKey(flight.departureAirportCode || flight.departureLocationName),
            destinationKeys: this.buildDestinationKeys(flight),
            activeDateKeys,
            seatsNeeded: typeof flight.seatsNeeded === 'number' ? flight.seatsNeeded : 0,
            seatsOffered: typeof flight.seatsOffered === 'number' ? flight.seatsOffered : 0,
            aircraftMaxSeats: flight.aircraftMaxSeats ?? null,
            isBringingPets: Boolean(flight.isBringingPets),
            isPetOk: flight.isPetOk ?? true,
            isBringingChildren: Boolean(flight.isBringingChildren),
            isChildrenOk: flight.isChildrenOk ?? true,
            isQuietFlightOnly: Boolean(flight.isQuietFlightOnly),
            expectedMood: flight.expectedMood ?? null,
            departureTimeExact: flight.departureTimeExact ?? null,
            departureTimeRangeStart: flight.departureTimeRangeStart ?? null,
            departureTimeRangeEnd: flight.departureTimeRangeEnd ?? null,
            updatedAt: new Date(),
        };
    }

    private hasAccessMembership(userProfile: any): boolean {
        const memberships = Array.isArray(userProfile?.memberships) ? userProfile.memberships : [];
        return Boolean(
            userProfile?.isHumanVerified ||
            userProfile?.idv?.status === 'verified' ||
            memberships.some((membership: any) =>
                membership?.statementVerified ||
                membership?.statementAccessGranted ||
                membership?.trustedContact ||
                membership?.provider === 'independent' ||
                membership?.organization === 'independent'
            )
        );
    }

}
